import { useEffect, useRef, useState } from 'react';
import {
  MIDDLEWARE_REQUEST_TIMEOUT_MS,
  TIMED_OUT_MESSAGE,
  getOrganisations,
  getTokenBounded,
} from '@repo/front-end-services';
import { matchTwinOrganisation } from './twinOrganisation';

/**
 * The name of the organisation a twin BELONGS TO, for the Equipment step's drop-zone target
 * (`equipment-page-start`, R4: *Uploading to {twin}* · *{organisation} · {route}*).
 *
 * **Never the operator's selected organisation.** A TL_ADMIN opens other organisations' twins as
 * a matter of course, so the selected organisation is a different one whenever they do — and a
 * wrong name beside "Uploading to" is the very mistake the target exists to prevent. The rule is
 * the one Go Live's `resolveActivationOrg` applies: the twin's `organisation_id`, matched by `id`
 * against the organisation list. That function is not reused because it fails closed with a BID;
 * this answers a display name or nothing.
 *
 * Returns `null` while the read is in flight, when the twin has no organisation, when the list
 * holds no organisation with that id (it returns only what the caller can see, and is unpaginated —
 * capped at the platform's 1,000, against 14 organisations on staging), and on any failure. The
 * caller then shows the route label alone.
 *
 * ## Bounds (`docs/coding-standards.md`, Outbound calls and list reads)
 *
 * One 30 s deadline (`MIDDLEWARE_REQUEST_TIMEOUT_MS`) across the token and the request; the
 * request's `signal` is the authoritative bound and axios's `timeout` the secondary guard. No
 * retry within a visit. An ANSWER — a name, or a list that holds no such organisation — is cached
 * for the life of the page, so returning to the step reads nothing. A FAILURE is not cached: it is
 * logged and the target shows the route label alone, and the next time the start screen shows it
 * reads again, so one blip does not take the twin's organisation off the target for the whole
 * session (review). A changed twin or an unmount aborts the read and drops its answer.
 */
export interface TwinOrganisationNameParams {
  /**
   * The twin's own `organisation_id`: a number; `null` when the twin is known and has none (logged
   * once, as Failure States asks); `undefined` while the twin itself is not known yet.
   */
  organisationId: number | null | undefined;
  /** For the logs only. */
  twinBid: string | null | undefined;
  /** Read only while the screen that shows the name is up. */
  active: boolean;
  /** The middleware base URL. */
  baseUrl: string;
  /** The raw Auth0 token getter; bounded here. */
  getToken: () => Promise<string>;
}

export const useTwinOrganisationName = ({
  organisationId,
  twinBid,
  active,
  baseUrl,
  getToken,
}: TwinOrganisationNameParams): string | null => {
  /** Answers already read: a name, or null for "read, and none". */
  const cache = useRef(new Map<number, string | null>());
  const [, setVersion] = useState(0);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  /** Twins already reported as having no organisation, so the warning is said once. */
  const warnedNoOrg = useRef(new Set<string>());

  useEffect(() => {
    if (!active || organisationId !== null) return;
    const key = twinBid ?? '';
    if (warnedNoOrg.current.has(key)) return;
    warnedNoOrg.current.add(key);
    // eslint-disable-next-line no-console
    console.warn('[equipment-start] organisation not found for twin', twinBid);
  }, [active, organisationId, twinBid]);

  useEffect(() => {
    if (!active || organisationId == null || cache.current.has(organisationId)) return;
    const id = organisationId;
    const controller = new AbortController();
    /** Set by the cleanup — a changed twin or an unmount — never by the deadline. */
    let cancelled = false;
    const deadline = Date.now() + MIDDLEWARE_REQUEST_TIMEOUT_MS;
    /** The deadline, not a cleanup, aborted the read — said as a timeout in the warning. */
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, MIDDLEWARE_REQUEST_TIMEOUT_MS);
    void (async () => {
      let organisations: Awaited<ReturnType<typeof getOrganisations>>;
      let reason = 'the organisation list could not be read';
      try {
        const token = await getTokenBounded(() => getTokenRef.current(), deadline - Date.now());
        const remaining = deadline - Date.now();
        if (remaining <= 0) throw new Error(TIMED_OUT_MESSAGE);
        organisations = await getOrganisations(baseUrl, token, {
          timeout: remaining,
          signal: controller.signal,
        });
      } catch (err) {
        organisations = undefined;
        reason = err instanceof Error ? err.message : reason;
      }
      // A changed twin or an unmount aborted this read: its answer is about nothing on screen.
      if (cancelled) return;
      if (!organisations) {
        // `getOrganisations` swallows its own errors and logs the status; say which kind this was.
        // eslint-disable-next-line no-console
        console.warn(
          '[equipment-start] could not read organisations',
          timedOut ? TIMED_OUT_MESSAGE : reason,
        );
        // Not cached: the next showing of the start screen reads again.
        return;
      }
      const match = matchTwinOrganisation(organisations, id);
      if (!match?.name) {
        // eslint-disable-next-line no-console
        console.warn('[equipment-start] organisation not found for twin', twinBid);
      }
      cache.current.set(id, match?.name || null);
      setVersion(v => v + 1);
    })();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
    // `twinBid` is for the log only; `getToken` is read through its ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, organisationId, baseUrl]);

  return organisationId == null ? null : (cache.current.get(organisationId) ?? null);
};
